import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'soft-lottie-animation-player',
  imports: [],
  templateUrl: './lottie-animation-player.component.html',
  styleUrl: './lottie-animation-player.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LottieAnimationPlayerComponent {
  readonly src = input.required<string>();
  readonly autoplay = input(true);
  readonly loop = input(false);

  protected readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>(
    'lottieAnimationCanvas'
  );

  protected lottiePlayer?: DotLottie;

  readonly resize$ = new Subject<UIEvent>();

  readonly loaded = output<void>();
  readonly play = output<void>();

  readonly isLoaded = signal(false);

  constructor() {
    const document = inject(DOCUMENT);
    const destroyRef = inject(DestroyRef);

    const resizeListener = (event: UIEvent) => {
      if (this.lottiePlayer) {
        this.resize$.next(event);
      }
    };

    const onLoaded = () => {
      this.isLoaded.set(true);
      this.loaded.emit();
    };

    const onPlayingStarted = () => {
      this.play.emit();
    };

    document.defaultView?.addEventListener('resize', resizeListener);

    // Clean up on destruction
    destroyRef.onDestroy(() => {
      document.defaultView?.removeEventListener('resize', resizeListener);

      if (this.lottiePlayer) {
        this.lottiePlayer.removeEventListener('load', onLoaded);
        this.lottiePlayer.removeEventListener('play', onPlayingStarted);

        this.lottiePlayer.stop();
        this.lottiePlayer.destroy();
        this.lottiePlayer = undefined;
      }
    });

    // Debounce the resize event to prevent unnecessary re-renders
    this.resize$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        if (this.lottiePlayer) {
          this.lottiePlayer?.resize();
        }
      });

    afterRenderEffect({
      write: () => {
        const src = this.src();
        const autoplay = this.autoplay();
        const loop = this.loop();
        const canvas = this.canvas();

        const config = {
          canvas: canvas?.nativeElement as HTMLCanvasElement,
          src,
          loop,
          autoplay,
          renderConfig: {
            devicePixelRatio: window?.devicePixelRatio || 1,
            autoResize: true,
          },
        };

        if (!this.lottiePlayer) {
          this.lottiePlayer = new DotLottie(config);
        }

        this.lottiePlayer?.addEventListener('load', onLoaded);
        this.lottiePlayer?.addEventListener('play', onPlayingStarted);

        this.lottiePlayer?.load(config);

        this.lottiePlayer?.setLoop(loop);
        this.lottiePlayer?.resize();
      },
    });
  }
}
